import { type ReactNode } from "react";
import {
  ChevronsUpDown,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { User } from "@lumika/shared";
import {
  ContactAvatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
  toast,
} from "@lumika/ui";
import { authApi } from "../api/auth";
import { useTheme } from "../hooks/useTheme";
import type { Theme } from "../lib/theme";
import { humanRoleLabel, userDisplayName } from "../lib/user";

type UserMenuVariant = "sidebar" | "compact";

type UserMenuProps = {
  /**
   * `sidebar` is the full identity row (avatar + name + email); `compact`
   * is the avatar-only trigger for the collapsed rail. The menu body is
   * identical for both — only the trigger and its placement differ.
   */
  variant: UserMenuVariant;
  user: Pick<
    User,
    "firstName" | "lastName" | "displayName" | "email" | "avatarUrl"
  > & { phone?: string | null };
  role: string;
};

// Theme picker segments in a stable light → dark → system order so the
// active pill never shifts position as the choice changes.
const THEME_OPTIONS: { value: Theme; label: string; icon: ReactNode }[] = [
  { value: "light", label: "Light", icon: <Sun className="h-4 w-4" /> },
  { value: "dark", label: "Dark", icon: <Moon className="h-4 w-4" /> },
  { value: "system", label: "System", icon: <Monitor className="h-4 w-4" /> },
];

/**
 * The single identity surface for the app shell. Replaces the three
 * separate "open a photo dialog" chips (topbar / sidebar footer / mobile
 * drawer) with one dropdown that owns account access, the theme choice,
 * and sign-out — so a person's name, role, and controls live in exactly
 * one place instead of three that had each drifted apart.
 */
export function UserMenu({ variant, user, role }: UserMenuProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const name = userDisplayName(user);
  // Email is the honest second line; when a user has none (a phone-only
  // teammate) fall back to the human role so the row is never blank.
  const secondary = user.email?.trim() || humanRoleLabel(role);

  async function signOut() {
    try {
      await authApi.logout();
    } catch {
      // The session cookie is cleared client-side on the next page load
      // regardless, so a dead network still logs the operator out locally
      // — say so rather than pretend the round-trip succeeded.
      toast.error("Signed out locally — server was unreachable");
    } finally {
      // Always leave the app. A hard navigation (not client routing) drops
      // every in-memory cache so the next session starts clean.
      window.location.assign("/sign-in");
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "sidebar" ? (
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md p-2 text-left outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ContactAvatar name={name} avatarUrl={user.avatarUrl} size="md" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{name}</span>
              <span className="truncate text-xs text-muted-foreground">
                {secondary}
              </span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Account menu — ${name}`}
            className="flex items-center justify-center rounded-md p-1 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ContactAvatar name={name} avatarUrl={user.avatarUrl} size="md" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-60"
        sideOffset={8}
        side={variant === "compact" ? "right" : "top"}
        align={variant === "compact" ? "end" : "start"}
      >
        {/* Non-interactive identity header — carries the name/email so the
            compact (avatar-only) trigger still names who is signed in. */}
        <div className="px-2 py-1.5">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          <p className="truncate text-xs text-muted-foreground">{secondary}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/account")}>
          <Settings className="h-4 w-4" />
          Account settings
        </DropdownMenuItem>
        {/* Quiet theme segmented control. Each segment is a menu item so it
            joins the menu's roving focus, but onSelect preventDefault keeps
            the panel open — you can flip Light/Dark and watch it apply. */}
        <div className="flex items-center gap-1 p-1">
          {THEME_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              aria-label={option.label}
              onSelect={(event) => {
                event.preventDefault();
                setTheme(option.value);
              }}
              className={cn(
                "h-7 flex-1 justify-center px-0",
                theme === option.value
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {option.icon}
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={signOut}>
          <LogOut className="h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
